/**
 * FiltersButton — « Filtres » button with active-filter badge.
 *
 * Shows « Filtres » or « Filtres · N » when N > 0 filters are active.
 * Active or panel open → violet-soft bg, border rgba(124,58,237,.3), text primary-dark.
 *
 * @see B-popup-frontend.md step 10b, UI-SPEC §1.2
 */
import { Box } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FiltersButtonProps {
  /** Number of currently active filters (activeChips(filterState).length). */
  nFilters: number;
  /** Whether the filter panel is currently open. */
  isOpen?: boolean;
  onClick: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FiltersButton({ nFilters, isOpen = false, onClick }: FiltersButtonProps) {
  const isActive = nFilters > 0 || isOpen;

  return (
    <Box
      component="button"
      onClick={onClick}
      aria-pressed={isOpen}
      title={nFilters > 0 ? `${nFilters} filtre(s) actif(s)` : 'Filtres'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 35,
        px: 1.25,
        border: '1px solid',
        borderColor: isActive ? 'rgba(124,58,237,.3)' : 'divider',
        borderRadius: 2,
        bgcolor: isActive ? 'rgba(124,58,237,.06)' : 'transparent',
        color: isActive ? '#4c1d95' : 'text.secondary',
        cursor: 'pointer',
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': {
          bgcolor: 'rgba(124,58,237,.06)',
          borderColor: 'rgba(124,58,237,.3)',
          color: '#4c1d95',
        },
      }}
    >
      <FilterAltIcon sx={{ fontSize: 15, opacity: 0.72 }} />
      <span>
        {/* i18n: promptStudio.filters.button */}
        {nFilters > 0 ? `Filtres · ${nFilters}` : 'Filtres'}
      </span>
    </Box>
  );
}
